import { snapToPixel } from '../util/math.js';
import { fieldMetrics } from '../field/metrics.js';
import type { ArcadeReplayBallSnapshot, ArcadeReplayFrameSnapshot, ArcadeReplayPackage, ArcadeReplayPlayerSnapshot } from '../../arcade/contracts.js';
import { arcadeDivingCatchVisualLiftYardsAtProgress } from '../../arcade/diving-catch.js';
import {
  applyArcadeReplayFrameToPlayers,
  blendArcadeReplayPlayersFromShell,
  defaultArcadeOwnerBallPointForReplayFrame,
  extendTouchdownPresentationFrames,
  mapArcadeFieldXToWorldX,
  mapArcadeFieldYToWorldY,
} from './arcade-render.js';
import { carriedBallPoint, centerSnapBallPoint } from '../player/model.js';
import {
  fumbleRecoveryHeadline,
  interceptionHeadline,
  isDefensiveFumbleRecovery,
  isInterceptionPlay,
} from './helpers.js';
import {
  divingHeroMomentAtFrame,
  divingHeroPresentationOffset,
  resolveDivingHeroMoment,
} from './diving-hero-moment.js';
import { presentFumbleTransitionFrames } from './turnover-cadence.js';

const PASS_SHELL_BLEND_FRAMES = 10;
const PASS_RELEASE_VISUAL_HEIGHT_YARDS = 1.42;
const DEFENSIVE_TOUCHDOWN_RUNOUT_FIELD_X = 7.75;
const INTERCEPTION_TD_FINISH_FRAMES = 18;
const INTERCEPTION_TD_HOLD_FRAMES = 12;

function clamp01(value: number) {
  return Math.max(0, Math.min(1, value));
}

function smoothstep(value: number) {
  const clamped = clamp01(value);
  return clamped * clamped * (3 - 2 * clamped);
}

function consecutiveIncompleteFrames(frames: ArcadeReplayFrameSnapshot[], startIndex: number) {
  let count = 0;
  for (let index = startIndex; index < frames.length; index += 1) {
    if (frames[index]?.ball?.state !== 'incomplete') {
      break;
    }
    count += 1;
  }
  return count;
}

function stabilizeIncompleteReplayFrames(frames: ArcadeReplayFrameSnapshot[]): ArcadeReplayFrameSnapshot[] {
  let settleState:
    | {
        startX: number;
        startY: number;
        startZ: number;
        driftX: number;
        driftY: number;
        settleY: number;
        bounceZ: number;
        totalFrames: number;
        frameIndex: number;
      }
    | null = null;
  let previousTravelDelta = { x: 0, y: 0 };
  let previousBall = frames[0]?.ball
    ? {
        x: frames[0].ball.x,
        y: frames[0].ball.y,
        z: frames[0].ball.z,
      }
    : null;

  return frames.map((frame, index) => {
    if (frame.ball.state !== 'incomplete') {
      settleState = null;
      if (previousBall) {
        previousTravelDelta = {
          x: frame.ball.x - previousBall.x,
          y: frame.ball.y - previousBall.y,
        };
      }
      previousBall = {
        x: frame.ball.x,
        y: frame.ball.y,
        z: frame.ball.z,
      };
      return frame;
    }

    if (!settleState) {
      const previousY = previousBall?.y ?? frame.ball.y;
      const previousZ = previousBall?.z ?? frame.ball.z;
      const travelX = Math.max(-0.08, Math.min(0.2, previousTravelDelta.x * 2.1));
      const travelY = Math.max(-0.04, Math.min(0.06, previousTravelDelta.y * 1.4));
      settleState = {
        startX: frame.ball.x,
        startY: frame.ball.y,
        startZ: Math.max(0, previousZ),
        driftX: travelX,
        driftY: travelY,
        settleY: 0.42 + Math.min(0.1, Math.abs(previousY - frame.ball.y) * 0.03),
        bounceZ: 0.24,
        totalFrames: Math.max(3, Math.min(6, consecutiveIncompleteFrames(frames, index))),
        frameIndex: 0,
      };
    }

    const progress = settleState.totalFrames <= 1
      ? 1
      : smoothstep(settleState.frameIndex / Math.max(1, settleState.totalFrames - 1));
    const bounceLift = settleState.frameIndex === 0
      ? 0
      : Math.sin(progress * Math.PI) * settleState.bounceZ * (1 - progress * 0.35);
    const nextBall = {
      ...frame.ball,
      x: settleState.startX + settleState.driftX * progress,
      y: settleState.startY + (settleState.settleY + settleState.driftY) * progress,
      z: Math.max(0, settleState.startZ * (1 - progress * 1.15) + bounceLift),
    };
    settleState.frameIndex += 1;
    previousBall = {
      x: nextBall.x,
      y: nextBall.y,
      z: nextBall.z,
    };
    return {
      ...frame,
      ball: nextBall,
    };
  });
}

export function applyDivingCatchVisualArc(
  frames: ArcadeReplayFrameSnapshot[],
): ArcadeReplayFrameSnapshot[] {
  const presentedFrames = frames.map((frame) => ({
    ...frame,
    players: frame.players.map((player) => ({ ...player })),
  }));
  const entityIds = new Set(
    presentedFrames.flatMap((frame) => (
      frame.players
        .filter((player) => String(player.assignmentKind || '').startsWith('catch-dive-'))
        .map((player) => player.entityId)
    )),
  );

  for (const entityId of entityIds) {
    let sequenceStart = -1;
    const applySequence = (sequenceEnd: number) => {
      if (sequenceStart < 0) return;
      const span = Math.max(1, sequenceEnd - sequenceStart);
      for (let diveFrameIndex = sequenceStart; diveFrameIndex <= sequenceEnd; diveFrameIndex += 1) {
        const divePlayer = presentedFrames[diveFrameIndex].players.find(
          (candidate) => candidate.entityId === entityId,
        );
        if (!divePlayer) continue;
        const progress = (diveFrameIndex - sequenceStart) / span;
        divePlayer.visualLiftYards = arcadeDivingCatchVisualLiftYardsAtProgress(progress);
      }
      sequenceStart = -1;
    };

    for (let frameIndex = 0; frameIndex <= presentedFrames.length; frameIndex += 1) {
      const player = frameIndex < presentedFrames.length
        ? presentedFrames[frameIndex].players.find((candidate) => candidate.entityId === entityId)
        : null;
      const assignment = String(player?.assignmentKind || '');
      const inDive = assignment === 'catch-dive-launch'
        || assignment === 'catch-dive-extension'
        || assignment === 'catch-dive-contact'
        || assignment === 'catch-dive-landing';
      if (inDive && sequenceStart < 0) {
        sequenceStart = frameIndex;
        continue;
      }
      if (inDive) {
        continue;
      }
      if (sequenceStart < 0) {
        continue;
      }
      applySequence(assignment === 'catch-dive-down' ? frameIndex : frameIndex - 1);
    }
  }

  return presentedFrames;
}

export function synchronizeDivingCatchWithPassArc(
  frames: ArcadeReplayFrameSnapshot[],
): ArcadeReplayFrameSnapshot[] {
  const diveStartIndex = frames.findIndex((frame) => (
    frame.players.some((player) => player.assignmentKind === 'catch-dive-launch')
  ));
  if (diveStartIndex < 0) {
    return frames;
  }
  const divingPlayer = frames[diveStartIndex].players.find(
    (player) => player.assignmentKind === 'catch-dive-launch',
  );
  if (!divingPlayer) {
    return frames;
  }
  const contactIndex = frames.findIndex((frame, frameIndex) => (
    frameIndex >= diveStartIndex
    && frame.players.some((player) => (
      player.entityId === divingPlayer.entityId
      && player.assignmentKind === 'catch-dive-contact'
    ))
  ));
  if (contactIndex <= diveStartIndex) {
    return frames;
  }

  const flightIndices = frames
    .map((frame, frameIndex) => ({ frame, frameIndex }))
    .filter(({ frame, frameIndex }) => frameIndex < diveStartIndex && frame.ball.state === 'pass-flight')
    .map(({ frameIndex }) => frameIndex);
  if (flightIndices.length < 4) {
    return frames;
  }

  const flightStartIndex = flightIndices[0];
  let arrivalIndex = flightIndices[0];
  for (const frameIndex of flightIndices.slice(1)) {
    const previousBall = frames[frameIndex - 1]?.ball;
    const currentBall = frames[frameIndex].ball;
    if (!previousBall) continue;
    const planarStep = Math.hypot(
      currentBall.x - previousBall.x,
      currentBall.y - previousBall.y,
    );
    const verticalStep = Math.abs(currentBall.z - previousBall.z);
    if (planarStep > 0.001 || verticalStep > 0.001) {
      arrivalIndex = frameIndex;
    }
  }
  if (arrivalIndex >= contactIndex) {
    return frames;
  }

  // Stretch the descending half of the throw so the receiver launches while
  // the ball is still traveling and reaches full extension as it arrives.
  const stretchStartIndex = Math.max(
    flightStartIndex,
    flightStartIndex + Math.floor((arrivalIndex - flightStartIndex) * 0.52),
  );
  const destinationSpan = Math.max(1, contactIndex - stretchStartIndex);
  const sourceSpan = Math.max(1, arrivalIndex - stretchStartIndex);

  return frames.map((frame, frameIndex) => {
    if (frameIndex < stretchStartIndex || frameIndex > contactIndex) {
      return frame;
    }
    const amount = (frameIndex - stretchStartIndex) / destinationSpan;
    const sourcePosition = stretchStartIndex + sourceSpan * amount;
    const leftIndex = Math.floor(sourcePosition);
    const rightIndex = Math.min(arrivalIndex, Math.ceil(sourcePosition));
    const ball = interpolateReplayBall(
      frames[leftIndex].ball,
      frames[rightIndex].ball,
      sourcePosition - leftIndex,
    );
    return {
      ...frame,
      ball: {
        ...ball,
        ownerId: null,
        ownerRendererRole: null,
        state: frameIndex === contactIndex ? 'catch-transfer' : 'pass-flight',
      },
    };
  });
}

function stabilizeBackwardTackleCloseoutReplayFrames(frames: ArcadeReplayFrameSnapshot[], play: any): ArcadeReplayFrameSnapshot[] {
  if (play?.arcadeReplay?.metadata?.catchResult !== 'complete') {
    return frames;
  }
  const targetRole = String(play?.participants?.targetRole || play?.arcadeReplay?.participants?.targetRole || '');
  const tacklerRole = String(play?.participants?.tacklerRole || play?.arcadeReplay?.participants?.tacklerRoles?.[0] || '');
  if (!targetRole || !tacklerRole) {
    return frames;
  }
  const catchFrame = play?.arcadeReplay?.events?.find?.((event: any) => event?.type === 'catch_complete')?.simFrame;
  const downFrame = play?.arcadeReplay?.events?.find?.((event: any) => event?.type === 'ballcarrier_down')?.simFrame;
  if (typeof catchFrame !== 'number' || typeof downFrame !== 'number') {
    return frames;
  }
  const catchIndex = frames.findIndex((frame) => frame.simFrame >= catchFrame);
  const downIndex = frames.findIndex((frame) => frame.simFrame >= downFrame);
  if (catchIndex < 0 || downIndex <= catchIndex + 1) {
    return frames;
  }

  let jumpIndex = -1;
  for (let index = catchIndex + 1; index <= downIndex; index += 1) {
    const previousTarget = frames[index - 1].players.find((player) => player.rendererRole === targetRole);
    const currentTarget = frames[index].players.find((player) => player.rendererRole === targetRole);
    if (!previousTarget || !currentTarget) {
      continue;
    }
    if (previousTarget.x - currentTarget.x >= 0.85) {
      jumpIndex = index;
      break;
    }
  }
  if (jumpIndex < 0) {
    return frames;
  }

  const startFrame = frames[jumpIndex - 1];
  const startTarget = startFrame.players.find((player) => player.rendererRole === targetRole);
  const startTackler = startFrame.players.find((player) => player.rendererRole === tacklerRole);
  if (!startTarget || !startTackler) {
    return frames;
  }
  const totalFrames = Math.max(1, downIndex - jumpIndex + 1);

  return frames.map((frame, frameIndex) => {
    if (frameIndex < jumpIndex || frameIndex > downIndex) {
      return frame;
    }
    const phase = Math.max(0, Math.min(1, (frameIndex - jumpIndex + 1) / totalFrames));
    const ease = phase * phase * (3 - 2 * phase);
    const carrierX = startTarget.x + Math.min(0.18, 0.04 + ease * 0.14);
    const rawTarget = frame.players.find((player) => player.rendererRole === targetRole);
    const carrierY = startTarget.y + ((rawTarget?.y ?? startTarget.y) - startTarget.y) * Math.min(0.35, ease);
    const tacklerTargetX = carrierX - 0.08;
    const tacklerTargetY = carrierY + 0.04;

    const players = frame.players.map((player) => {
      if (player.rendererRole === targetRole) {
        return {
          ...player,
          x: carrierX,
          y: carrierY,
          vx: Math.max(0, player.vx),
        };
      }
      if (player.rendererRole === tacklerRole) {
        return {
          ...player,
          x: startTackler.x + (tacklerTargetX - startTackler.x) * ease,
          y: startTackler.y + (tacklerTargetY - startTackler.y) * ease,
          vx: Math.max(0, player.vx),
        };
      }
      return player;
    });
    const ball = frame.ball.ownerRendererRole === targetRole
      ? {
          ...frame.ball,
          x: carrierX + 0.28,
          y: carrierY + 0.02,
        }
      : frame.ball;
    return {
      ...frame,
      players,
      ball,
    };
  });
}

function ownerBallPointForReplayFrame(
  replayFrame: ArcadeReplayFrameSnapshot,
  direction: number,
  metrics: ReturnType<typeof fieldMetrics>,
  renderedPlayers: any[],
) {
  if (replayFrame.ball.state !== 'carried' || !replayFrame.ball.ownerRendererRole) {
    return null;
  }

  const owner = replayFrame.players.find((player) => player.rendererRole === replayFrame.ball.ownerRendererRole);
  const renderedOwner = renderedPlayers.find((player) => player.role === replayFrame.ball.ownerRendererRole);
  if (!owner) {
    return null;
  }

  const pocketCarrier = owner.rendererRole === 'QB'
    && owner.side === 'offense'
    && (owner.assignmentKind === 'dropback'
      || owner.assignmentKind === 'pressure'
      || owner.assignmentKind === 'pocket-climb'
      || owner.assignmentKind === 'pocket-drift'
      || owner.assignmentKind === 'pocket-reset'
      || owner.assignmentKind === 'set-trigger'
      || owner.assignmentKind === 'snap-hands'
      || owner.assignmentKind === 'throwing'
      || owner.assignmentKind === 'throw-follow'
      || owner.state === 'set');
  if (pocketCarrier) {
    const pocketOwner = renderedOwner || {
      role: owner.rendererRole,
      x: mapArcadeFieldXToWorldX(owner.x, direction),
      y: mapArcadeFieldYToWorldY(owner.y, metrics),
      moving: Math.hypot(owner.vx, owner.vy) > 0.06,
    };
    const carryPoint = carriedBallPoint(pocketOwner as any, pocketOwner.x, pocketOwner.y, direction, 'offense');
    return {
      ballX: carryPoint.ballX,
      ballY: carryPoint.ballY,
      arcadeState: replayFrame.ball.state,
      arcadeOwnerRole: replayFrame.ball.ownerRendererRole,
    };
  }
  return defaultArcadeOwnerBallPointForReplayFrame(
    replayFrame,
    direction,
    metrics,
    [...renderedPlayers],
    { handYOffset: 8 },
  );
}

function ballPointForReplayFrame(
  replayFrame: ArcadeReplayFrameSnapshot,
  direction: number,
  metrics: ReturnType<typeof fieldMetrics>,
  losX: number,
  offensePlayers: any[],
  defensePlayers: any[],
) {
  const snapper = offensePlayers.find((player) => player.role === 'C');
  if (replayFrame.ball.state === 'snap' && snapper) {
    return {
      ...centerSnapBallPoint(snapper as any, direction, losX, snapper.y + 8),
      arcadeState: replayFrame.ball.state,
      arcadeOwnerRole: replayFrame.ball.ownerRendererRole,
    };
  }

  const ownerPoint = ownerBallPointForReplayFrame(
    replayFrame,
    direction,
    metrics,
    [...offensePlayers, ...defensePlayers],
  );
  if (ownerPoint) {
    return ownerPoint;
  }

  // The carried-ball pose already renders at hand height. Project only the
  // height above the release plane during flight so the first airborne frame
  // continues from the QB's hand instead of jumping upward by two yards.
  const flightLift = replayFrame.ball.state === 'pass-flight'
    ? Math.max(0, replayFrame.ball.z - PASS_RELEASE_VISUAL_HEIGHT_YARDS) * 14
    : replayFrame.ball.state === 'tip'
      ? replayFrame.ball.z * 10
      : replayFrame.ball.z * 4;
  return {
    ballX: mapArcadeFieldXToWorldX(replayFrame.ball.x, direction),
    ballY: snapToPixel(mapArcadeFieldYToWorldY(replayFrame.ball.y, metrics) + 8 - flightLift),
    arcadeHeight: Math.max(0, replayFrame.ball.z),
    arcadeState: replayFrame.ball.state,
    arcadeOwnerRole: replayFrame.ball.ownerRendererRole,
  };
}

function smoothCompletedCatchCarryBallFrames(liveFrames: any[], replayFrames: ArcadeReplayFrameSnapshot[]): any[] {
  if (liveFrames.length < 2) {
    return liveFrames;
  }
  const smoothed = liveFrames.map((frame) => ({
    ...frame,
    ball: frame.ball ? { ...frame.ball } : frame.ball,
  }));

  for (let index = 1; index < smoothed.length; index += 1) {
    const previousReplayBall = replayFrames[index - 1]?.ball;
    const currentReplayBall = replayFrames[index]?.ball;
    if (
      previousReplayBall?.state !== 'catch-transfer'
      || currentReplayBall?.state !== 'carried'
      || !currentReplayBall.ownerRendererRole
    ) {
      continue;
    }

    const startBall = smoothed[index - 1].ball;
    const firstCarryBall = smoothed[index].ball;
    const jumpDistance = Math.hypot(
      Number(firstCarryBall?.ballX || 0) - Number(startBall?.ballX || 0),
      Number(firstCarryBall?.ballY || 0) - Number(startBall?.ballY || 0),
    );
    if (jumpDistance < 6) {
      continue;
    }

    const blendFrames = Math.max(4, Math.min(7, Math.round(jumpDistance / 3)));
    for (let offset = 0; offset < blendFrames && index + offset < smoothed.length; offset += 1) {
      const frame = smoothed[index + offset];
      const replayBall = replayFrames[index + offset]?.ball;
      if (replayBall?.state !== 'carried' || replayBall.ownerRendererRole !== currentReplayBall.ownerRendererRole) {
        break;
      }
      const targetBall = liveFrames[index + offset].ball;
      const ease = smoothstep((offset + 1) / blendFrames);
      frame.ball = {
        ...targetBall,
        ballX: snapToPixel(Number(startBall.ballX || 0) + (Number(targetBall.ballX || 0) - Number(startBall.ballX || 0)) * ease),
        ballY: snapToPixel(Number(startBall.ballY || 0) + (Number(targetBall.ballY || 0) - Number(startBall.ballY || 0)) * ease),
      };
    }
  }

  return smoothed;
}

function smoothTacklerCloseLiveFrames(liveFrames: any[], play: any): any[] {
  if (play?.arcadeReplay?.metadata?.catchResult !== 'complete') {
    return liveFrames;
  }
  const targetRole = String(play?.participants?.targetRole || play?.arcadeReplay?.participants?.targetRole || '');
  const tacklerRole = String(play?.participants?.tacklerRole || play?.arcadeReplay?.participants?.tacklerRoles?.[0] || '');
  if (!targetRole || !tacklerRole) {
    return liveFrames;
  }
  const catchFrame = play?.arcadeReplay?.events?.find?.((event: any) => event?.type === 'catch_complete')?.simFrame;
  const downFrame = play?.arcadeReplay?.events?.find?.((event: any) => event?.type === 'ballcarrier_down')?.simFrame;
  if (typeof catchFrame !== 'number' || typeof downFrame !== 'number') {
    return liveFrames;
  }

  const smoothed = liveFrames.map((frame) => ({
    ...frame,
    offensePlayers: frame.offensePlayers.map((player: any) => ({ ...player })),
    defensePlayers: frame.defensePlayers.map((player: any) => ({ ...player })),
  }));
  const maxTacklerStep = 8.25;

  for (let index = 1; index < smoothed.length; index += 1) {
    const frame = smoothed[index];
    if (frame.simFrame < catchFrame || frame.simFrame > downFrame) {
      continue;
    }
    const previousPlayers = [...smoothed[index - 1].offensePlayers, ...smoothed[index - 1].defensePlayers];
    const currentGroup = frame.offensePlayers.some((player: any) => player.role === tacklerRole)
      ? frame.offensePlayers
      : frame.defensePlayers;
    const tackler = currentGroup.find((player: any) => player.role === tacklerRole);
    const previousTackler = previousPlayers.find((player: any) => player.role === tacklerRole);
    const target = [...frame.offensePlayers, ...frame.defensePlayers].find((player: any) => player.role === targetRole);
    if (!tackler || !previousTackler || !target) {
      continue;
    }

    const stepX = tackler.x - previousTackler.x;
    const stepY = tackler.y - previousTackler.y;
    const stepDistance = Math.hypot(stepX, stepY);
    if (stepDistance <= maxTacklerStep) {
      continue;
    }

    const stepScale = maxTacklerStep / Math.max(0.001, stepDistance);
    const cappedX = previousTackler.x + stepX * stepScale;
    const cappedY = previousTackler.y + stepY * stepScale;
    const distanceToTarget = Math.hypot(target.x - cappedX, target.y - cappedY);
    tackler.x = snapToPixel(cappedX);
    tackler.y = snapToPixel(cappedY);
    tackler.moving = distanceToTarget > 4 ? tackler.moving : false;
  }

  return smoothed;
}

export function buildArcadePassLiveSequence(context: any): any[] {
  const replay = context.play?.arcadeReplay as ArcadeReplayPackage | undefined;
  if (!replay?.frames?.length) {
    return [];
  }

  const turnoverReplayFrames = presentFumbleTransitionFrames(replay);
  const rawReplayFrames = extendTouchdownPresentationFrames(
    presentInterceptionReplayFrames(turnoverReplayFrames, context.play),
    replay.stopReason === 'touchdown',
  );
  const replayFrames = applyDivingCatchVisualArc(
    synchronizeDivingCatchWithPassArc(stabilizeIncompleteReplayFrames(rawReplayFrames)),
  );
  const divingHeroPlan = resolveDivingHeroMoment(context.play);
  const metrics = fieldMetrics();
  const interception = isInterceptionPlay(context.play);
  const defensiveFumbleRecovery = isDefensiveFumbleRecovery(context.play);
  const interceptionRole = String(context.play?.participants?.interceptorRole || replay.participants?.interceptorRole || "") || null;
  const recovererRole = String(context.play?.participants?.recovererRole || replay.participants?.recovererRole || "") || null;
  const interceptionEvent = interception
    ? replay.events.find((event: any) => event?.type === 'pass_intercepted')
      || replay.keyEvents?.catchResult
      || replay.keyEvents?.catch
      || null
    : null;
  const fumbleRecoveryEvent = defensiveFumbleRecovery
    ? replay.events.find((event: any) => event?.type === 'fumble_recovered') || null
    : null;
  const takeoverEvent = interception ? interceptionEvent : fumbleRecoveryEvent;
  const takeoverFrameIndex = typeof takeoverEvent?.simFrame === 'number'
    ? replayFrames.findIndex((frame) => frame.simFrame >= takeoverEvent.simFrame)
    : -1;
  const takeoverRole = interception ? interceptionRole : defensiveFumbleRecovery ? recovererRole : null;
  const takeoverCallout = interception
    ? interceptionHeadline()
    : defensiveFumbleRecovery
      ? fumbleRecoveryHeadline(context.play)
      : null;

  const liveFrames = replayFrames.map((replayFrame, replayFrameIndex) => {
    const previousReplayFrame = replayFrames[Math.max(0, replayFrameIndex - 4)] || replayFrame;
    const offensePlayers = blendArcadeReplayPlayersFromShell(
      context.formation.offense,
      applyArcadeReplayFrameToPlayers(context.formation.offense, replayFrame, context.direction, metrics, {
        movingThreshold: 0.06,
        previousReplayFrame,
      }),
      replayFrame,
      { blendFrames: PASS_SHELL_BLEND_FRAMES, revealMotionAt: 0.45 },
    );
    const defensePlayers = blendArcadeReplayPlayersFromShell(
      context.formation.defense,
      applyArcadeReplayFrameToPlayers(context.formation.defense, replayFrame, context.direction, metrics, {
        movingThreshold: 0.06,
        previousReplayFrame,
      }),
      replayFrame,
      { blendFrames: PASS_SHELL_BLEND_FRAMES, revealMotionAt: 0.45 },
    );
    const takeoverFrames = takeoverFrameIndex >= 0 ? replayFrameIndex - takeoverFrameIndex : -1;

    const liveFrame: any = {
      simFrame: replayFrame.simFrame,
      offensePlayers,
      defensePlayers,
      ball: ballPointForReplayFrame(replayFrame, context.direction, metrics, context.losX, offensePlayers, defensePlayers),
    };
    const heroMoment = divingHeroMomentAtFrame(divingHeroPlan, replayFrame.simFrame);
    if (heroMoment) {
      liveFrame.heroMoment = heroMoment;
      const heroPlayers = heroMoment.teamType === 'defense' ? defensePlayers : offensePlayers;
      const heroPlayer = heroPlayers.find((player: any) => player.role === heroMoment.role);
      const nativeDive = String(heroPlayer?.arcadeAssignment || '').startsWith('catch-dive-');
      if (!nativeDive && heroMoment.result === 'completion') {
        const diveOffset = divingHeroPresentationOffset(divingHeroPlan, replayFrame.simFrame);
        const forwardPixels = Number(diveOffset?.forwardYards || 0) * metrics.yardWidth * context.direction;
        const lift = Number(diveOffset?.liftPixels || 0);
        const decoratePlayer = (player: any) => player.role === heroMoment.role
          ? {
              ...player,
              x: player.x + forwardPixels,
              y: player.y - lift,
              arcadeVerticalLift: lift,
              state: context.direction > 0 ? 'fallRight' : 'fallLeft',
            }
          : player;
        if (heroMoment.teamType === 'defense') liveFrame.defensePlayers = defensePlayers.map(decoratePlayer);
        else liveFrame.offensePlayers = offensePlayers.map(decoratePlayer);
        if (liveFrame.ball) liveFrame.ball = { ...liveFrame.ball, x: liveFrame.ball.x + forwardPixels };
      }
    }
    const divingPlayerEntry = [
      { players: offensePlayers, teamType: 'offense' },
      { players: defensePlayers, teamType: 'defense' },
    ].map((entry) => ({
      ...entry,
      player: entry.players.find((player: any) => (
        player.arcadeAssignment === 'catch-dive-landing'
        || player.arcadeAssignment === 'catch-dive-down'
      )),
    })).find((entry) => entry.player);
    if (divingPlayerEntry?.player) {
      const phase = divingPlayerEntry.player.arcadeAssignment === 'catch-dive-landing'
        ? 'landing'
        : 'down';
      liveFrame.catchFlair = {
        role: divingPlayerEntry.player.role,
        teamType: divingPlayerEntry.teamType,
        phase,
        intensity: phase === 'landing' ? 0.68 : 0.28,
      };
    }
    if (takeoverCallout && takeoverFrames >= 0 && takeoverFrames < 18) {
      liveFrame.ribbonMessage = takeoverCallout;
    }
    if (takeoverRole && takeoverFrames >= 0 && takeoverFrames < 12) {
      liveFrame.takeawayFlash = {
        role: takeoverRole,
        teamType: 'defense',
        intensity: Math.max(0, Math.min(1, 1 - takeoverFrames / 12)),
        color: takeoverCallout?.color,
      };
    }
    const requestedCelebration = context.play?.heroPresentation?.celebration;
    const celebration = replay.stopReason === 'touchdown'
      ? requestedCelebration === 'signature' ? 'signature' : 'none'
      : null;
    const scorerRole = String(
      context.play?.participants?.targetRole
        || context.play?.participants?.runnerRole
        || replay.participants?.targetRole
        || replayFrame.ball.ownerRendererRole
        || '',
    );
    const celebrationStart = Math.max(0, replayFrames.length - 42);
    if (replay.stopReason === 'touchdown' && celebration && scorerRole && replayFrameIndex >= celebrationStart) {
      const progress = (replayFrameIndex - celebrationStart) / Math.max(1, replayFrames.length - celebrationStart - 1);
      liveFrame.celebrationMoment = {
        role: scorerRole,
        teamType: interception ? 'defense' : 'offense',
        style: celebration,
        playerName: context.play?.heroPresentation?.playerName || null,
        progress,
        intensity: Math.sin(progress * Math.PI),
      };
      const playerPool = interception ? liveFrame.defensePlayers : liveFrame.offensePlayers;
      const posedPlayers = playerPool.map((player: any) => player.role === scorerRole
        ? { ...player, moving: false }
        : player);
      if (interception) liveFrame.defensePlayers = posedPlayers;
      else liveFrame.offensePlayers = posedPlayers;
    }
    return liveFrame;
  });
  return smoothCompletedCatchCarryBallFrames(liveFrames, replayFrames);
}

function presentInterceptionReplayFrames(frames: ArcadeReplayFrameSnapshot[], play: any): ArcadeReplayFrameSnapshot[] {
  if (!isInterceptionPlay(play)) {
    return frames;
  }
  if (play?.defensiveScore || play?.stopReason === 'touchdown') {
    return presentInterceptionTouchdownReplayFrames(frames, play);
  }

  return presentInterceptionTackleReplayFrames(frames, play);
}

function presentInterceptionTackleReplayFrames(frames: ArcadeReplayFrameSnapshot[], play: any): ArcadeReplayFrameSnapshot[] {
  const takeoverFrame = play?.arcadeReplay?.events?.find?.((event: any) => event?.type === 'pass_intercepted')?.simFrame
    ?? play?.arcadeReplay?.keyEvents?.catchResult?.simFrame
    ?? play?.arcadeReplay?.keyEvents?.catch?.simFrame;
  const tackleFrame = play?.arcadeReplay?.events?.find?.((event: any) => event?.type === 'tackle_started')?.simFrame
    ?? play?.arcadeReplay?.keyEvents?.tackle?.simFrame;
  if (typeof takeoverFrame !== 'number' || typeof tackleFrame !== 'number') {
    return frames;
  }
  const takeoverIndex = frames.findIndex((frame) => frame.simFrame >= takeoverFrame);
  const tackleIndex = frames.findIndex((frame) => frame.simFrame >= tackleFrame);
  if (takeoverIndex < 0 || tackleIndex <= takeoverIndex + 1) {
    return frames;
  }

  // The authoritative replay already contains the complete pursuit and tackle.
  // Stretching a one- or two-yard return to a large fixed frame budget makes
  // every player appear to enter slow motion as soon as possession changes.
  return frames;
}

function presentInterceptionTouchdownReplayFrames(frames: ArcadeReplayFrameSnapshot[], play: any): ArcadeReplayFrameSnapshot[] {
  const takeoverFrame = play?.arcadeReplay?.events?.find?.((event: any) => event?.type === 'pass_intercepted')?.simFrame
    ?? play?.arcadeReplay?.keyEvents?.catchResult?.simFrame
    ?? play?.arcadeReplay?.keyEvents?.catch?.simFrame;
  const touchdownFrame = play?.arcadeReplay?.events?.find?.((event: any) => event?.type === 'touchdown')?.simFrame;
  if (typeof takeoverFrame !== 'number' || typeof touchdownFrame !== 'number') {
    return frames;
  }
  const takeoverIndex = frames.findIndex((frame) => frame.simFrame >= takeoverFrame);
  const touchdownIndex = frames.findIndex((frame) => frame.simFrame >= touchdownFrame);
  if (takeoverIndex < 0 || touchdownIndex <= takeoverIndex) {
    return frames;
  }
  const preTakeoverFrames = frames.slice(0, takeoverIndex + 1);
  const returnFrames = frames.slice(takeoverIndex + 1, touchdownIndex + 1);
  const touchdownFrameSnapshot = frames[touchdownIndex];
  const finishFrameSnapshot = createInterceptionTouchdownFinishFrame(touchdownFrameSnapshot, play);
  const finishFrames = interpolateFramePair(
    touchdownFrameSnapshot,
    finishFrameSnapshot,
    INTERCEPTION_TD_FINISH_FRAMES,
    { playState: 'touchdown-runout', simFrameStart: touchdownFrameSnapshot.simFrame + 1 },
  );
  const holdSourceFrame = finishFrames[finishFrames.length - 1] || finishFrameSnapshot;
  const holdFrames = Array.from({ length: INTERCEPTION_TD_HOLD_FRAMES }, (_, index) => ({
    ...holdSourceFrame,
    players: holdSourceFrame.players.map((player) => ({ ...player, vx: 0, vy: 0 })),
    ball: { ...holdSourceFrame.ball, vx: 0, vy: 0 },
    collisions: [],
    contacts: [],
    simFrame: holdSourceFrame.simFrame + index + 1,
    time: holdSourceFrame.time + (index + 1) / 60,
    playState: 'touchdown-runout',
  }));

  return [...preTakeoverFrames, ...returnFrames, ...finishFrames, ...holdFrames];
}

function interpolateFramePair(
  from: ArcadeReplayFrameSnapshot,
  to: ArcadeReplayFrameSnapshot,
  count: number,
  options: { playState?: string; simFrameStart?: number } = {},
): ArcadeReplayFrameSnapshot[] {
  const frames: ArcadeReplayFrameSnapshot[] = [];
  const total = Math.max(1, count);
  for (let index = 0; index < total; index += 1) {
    const amount = (index + 1) / total;
    const frame = interpolateReplayFrames(from, to, smoothstep(amount));
    frames.push({
      ...frame,
      simFrame: (options.simFrameStart ?? from.simFrame + 1) + index,
      time: from.time + (index + 1) / 60,
      playState: options.playState ?? frame.playState,
      collisions: [],
      contacts: [],
    });
  }
  return frames;
}

function interpolateReplayFrames(
  left: ArcadeReplayFrameSnapshot,
  right: ArcadeReplayFrameSnapshot,
  amount: number,
): ArcadeReplayFrameSnapshot {
  const blend = clamp01(amount);
  return {
    ...right,
    simFrame: Math.round(lerp(left.simFrame, right.simFrame, blend)),
    time: lerp(left.time, right.time, blend),
    players: interpolateReplayPlayers(left.players, right.players, blend),
    ball: interpolateReplayBall(left.ball, right.ball, blend),
    collisions: blend < 0.5 ? left.collisions : right.collisions,
    contacts: blend < 0.5 ? left.contacts : right.contacts,
  };
}

function interpolateReplayPlayers(
  leftPlayers: ArcadeReplayPlayerSnapshot[],
  rightPlayers: ArcadeReplayPlayerSnapshot[],
  amount: number,
): ArcadeReplayPlayerSnapshot[] {
  return leftPlayers.map((leftPlayer) => {
    const rightPlayer = rightPlayers.find((candidate) => candidate.entityId === leftPlayer.entityId)
      || rightPlayers.find((candidate) => candidate.rendererRole === leftPlayer.rendererRole && candidate.side === leftPlayer.side)
      || leftPlayer;
    return {
      ...rightPlayer,
      x: lerp(leftPlayer.x, rightPlayer.x, amount),
      y: lerp(leftPlayer.y, rightPlayer.y, amount),
      vx: lerp(leftPlayer.vx, rightPlayer.vx, amount),
      vy: lerp(leftPlayer.vy, rightPlayer.vy, amount),
      facing: lerp(leftPlayer.facing, rightPlayer.facing, amount),
      tackleProgress: lerp(leftPlayer.tackleProgress, rightPlayer.tackleProgress, amount),
    };
  });
}

function interpolateReplayBall(
  left: ArcadeReplayBallSnapshot,
  right: ArcadeReplayBallSnapshot,
  amount: number,
): ArcadeReplayBallSnapshot {
  return {
    ...right,
    x: lerp(left.x, right.x, amount),
    y: lerp(left.y, right.y, amount),
    z: lerp(left.z, right.z, amount),
    vx: lerp(left.vx, right.vx, amount),
    vy: lerp(left.vy, right.vy, amount),
  };
}

function createInterceptionTouchdownFinishFrame(
  touchdownFrame: ArcadeReplayFrameSnapshot,
  play: any,
): ArcadeReplayFrameSnapshot {
  const carrierRole = String(
    play?.participants?.interceptorRole
      || play?.arcadeReplay?.participants?.interceptorRole
      || touchdownFrame.ball.ownerRendererRole
      || '',
  ) || null;
  const carrier = touchdownFrame.players.find((player) => player.rendererRole === carrierRole)
    || touchdownFrame.players.find((player) => player.entityId === touchdownFrame.ball.ownerId)
    || null;
  const targetX = DEFENSIVE_TOUCHDOWN_RUNOUT_FIELD_X;
  const targetY = carrier?.y ?? touchdownFrame.ball.y;
  const carrierId = carrier?.entityId ?? touchdownFrame.ball.ownerId;
  const finishPlayers = touchdownFrame.players.map((player) => {
    const isCarrier = (carrierId && player.entityId === carrierId)
      || (carrierRole && player.rendererRole === carrierRole);
    if (!isCarrier) {
      return {
        ...player,
        beingTackledBy: [],
        tackleProgress: 0,
        engagementState: player.engagementState === 'tackled' ? null : player.engagementState,
      };
    }
    return {
      ...player,
      x: targetX,
      y: targetY,
      vx: -0.18,
      vy: 0,
      facing: -1,
      state: 'return-score',
      assignmentKind: 'touchdown-runout',
      engagementState: null,
      beingTackledBy: [],
      tackleProgress: 0,
    };
  });
  return {
    ...touchdownFrame,
    playState: 'touchdown-runout',
    players: finishPlayers,
    ball: {
      ...touchdownFrame.ball,
      x: targetX,
      y: targetY,
      z: 0,
      vx: -0.18,
      vy: 0,
      state: 'carried',
      ownerId: carrierId ?? touchdownFrame.ball.ownerId,
      ownerRendererRole: carrierRole ?? touchdownFrame.ball.ownerRendererRole,
    },
    collisions: [],
    contacts: [],
  };
}

function lerp(start: number, end: number, amount: number) {
  return start + (end - start) * amount;
}
